import type { Node } from 'ricos-schema';
import { Node_Type, RichContent } from 'ricos-schema';

import { getParagraphNode } from '../../draft/toDraft/decorationParsers';
import type { ToPlainTextGenerator } from './convertNodes';
import {
  parseAppEmbed,
  parseAudio,
  parseEmbed,
  parseGiphy,
  parseImage,
  parseLinkPreview,
  parseListNode,
  parseMap,
  parseTextNodes,
  parseVideo,
} from './convertNodes';

// We are having this function here instead of './convertNodes' to avoid circular dependency
function* parseCollapsible(node: Node, delimiter?: string) {
  const collapsibleTextNodes: string[] = [];

  // eslint-disable-next-line fp/no-loops
  for (const child of node.nodes ?? []) {
    const [first, second] = child.nodes ?? [];
    if (!first || !second) {
      continue;
    }
    collapsibleTextNodes.push(
      [yield* toPlainTextBase(first), yield* toPlainTextBase(second)].join(delimiter)
    );
  }

  return collapsibleTextNodes.join(delimiter);
}

/*
 * This function is used to convert a RichContent to plain text.
 * It is a generator function that yields the following commands:
 * - getImageUrl: returns the image url
 * - getVideoUrl: returns the video url
 * - getAudioUrl: returns the audio url
 * It also receives a delimiter string that is used to separate the different nodes.
 * The default delimiter is a new line.
 * The function returns a string that is the plain text representation of the RichContent.
 *
 * It's a generator function because it needs to yield the commands to get the urls of the
 * media which can be both sync and async.
 */
export function* toPlainTextBase(
  content: RichContent,
  delimiter = '\n',
  addLinksToText = true
): ToPlainTextGenerator {
  const ricosContent = RichContent.fromJSON(content);
  const { nodes = [] } = ricosContent;
  let plainText = '';

  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index];
    if (node) {
      if (index > 0) {
        plainText += delimiter;
      }
      switch (node.type) {
        case Node_Type.CODE_BLOCK:
        case Node_Type.PARAGRAPH:
        case Node_Type.HEADING:
          plainText += parseTextNodes(node, addLinksToText);
          break;
        case Node_Type.BLOCKQUOTE:
          plainText += parseTextNodes(getParagraphNode(node), addLinksToText);
          break;
        case Node_Type.ORDERED_LIST:
        case Node_Type.BULLETED_LIST:
          plainText += parseListNode(node, delimiter, addLinksToText);
          break;
        case Node_Type.GIF:
          plainText += yield* parseGiphy(node);
          break;
        case Node_Type.MAP:
          plainText += parseMap(node);
          break;
        case Node_Type.APP_EMBED:
          plainText += parseAppEmbed(node, delimiter);
          break;
        case Node_Type.LINK_PREVIEW:
          plainText += parseLinkPreview(node);
          break;
        case Node_Type.EMBED:
          plainText += parseEmbed(node);
          break;
        case Node_Type.IMAGE:
          plainText += yield* parseImage(node, delimiter);
          break;
        case Node_Type.VIDEO:
          plainText += yield* parseVideo(node, delimiter);
          break;
        case Node_Type.AUDIO:
          plainText += yield* parseAudio(node, delimiter);
          break;
        case Node_Type.COLLAPSIBLE_LIST:
          plainText += yield* parseCollapsible(node, delimiter);
          break;
        default:
      }
    }
  }

  return plainText;
}
